import * as Sentry from "@sentry/react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { backendApiScopes, loginRequest } from "./main";
import { setAuthTokenProvider } from "./api/apiClient";
import { useWebSocket } from "./hooks/useWebSocket";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { apiUrl } from "./utils/apiBase";

import WorkCenterSelector from "./components/WorkCenterSelector";
import QuoteSelector from "./components/QuoteSelector";
import OrderSelector from "./components/OrderSelector";
import OperationSelector from "./components/OperationSelector";
import JobTracker from "./components/JobTracker";
import JobDetailDrawer from "./components/JobDetailDrawer";
import SessionDashboard from "./components/SessionDashboard";
import CompleteModal from "./components/CompleteModal";
import MultiCompleteModal from "./components/MultiCompleteModal";
import PMDashboard from "./components/PMDashboard";
import PADashboard from "./components/PADashboard";
import WorkCenterSchedule from "./components/WorkCenterSchedule";
import ProjectSchedule from "./components/ProjectSchedule";
import ResourceLoadDashboard from "./components/ResourceLoadDashboard";
import PrinterSelectModal from "./components/PrinterSelectModal";
import ReworkTypeModal from "./components/ReworkTypeModal";
import ReworkCauseModal from "./components/ReworkCauseModal";
import ReprintModal from "./components/ReprintModal";
import NotificationSettings from "./components/NotificationSettings";
import KeyboardShortcutsHelp from "./components/KeyboardShortcutsHelp";
import PinnedJobs, { usePinnedJobs } from "./components/PinnedJobs";
import MyPinnedQuotes, { PinnedQuotesProvider } from "./components/MyPinnedQuotes";
import MyWork from "./components/MyWork";
import FeedbackModal from "./components/FeedbackModal";
import FeedbackDashboard from "./components/FeedbackDashboard";
import ReportsDashboard from "./components/ReportsDashboard";
import ThroughputReport from "./components/ThroughputReport";
import PerformanceReport from "./components/PerformanceReport";
import TeamView from "./components/TeamView";
import PMCommandCenter from "./components/PMCommandCenter";
import PMInterface from "./components/pm/PMInterface";
import PMWorkOrderDemo from "./components/PMWorkOrderDemo";
import PMWorkOrderGenerator from "./components/PMWorkOrderGenerator";
import OrderView from "./components/OrderView";
import ProductionScheduleBoard from "./components/ProductionScheduleBoard";
import UnifiedProjectBoard from "./components/UnifiedProjectBoard";
import AdminTickets from "./components/AdminTickets";
import AdminUserManagement from "./components/AdminUserManagement";
import DataQualityDashboard from "./components/DataQualityDashboard";
import NotificationAdmin from "./components/admin/NotificationAdmin";
import WorkInstructionsPage from "./components/WorkInstructionsPage";
import ProductionFloorView from "./components/production/ProductionFloorView";
import ProjectProductionView from "./components/production/ProjectProductionView";
import ProductionView from "./components/production/ProductionView";
import PAQueue from "./components/queues/PAQueue";
import ProjectDetailPage from "./components/project/ProjectDetailPage";
import AssemblyQueue from "./components/queues/AssemblyQueue";
import ReworkModal from "./components/ReworkModal";
import QuickReworkModal from "./components/QuickReworkModal";
import ReprintRemakeModal from "./components/ReprintRemakeModal";
import UpdateBanner from "./components/UpdateBanner";
import AppHeader from "./components/AppHeader";
import MainNavigation from "./components/navigation/MainNavigation";
import PMInstantCompleteModal from "./components/PMInstantCompleteModal";
import TodaysSessions from "./components/TodaysSessions";
import PendingQuoteCompleteModal from "./components/PendingQuoteCompleteModal";
import NotificationBell from "./components/NotificationBell";
import PendingQuoteConfirmModal from "./components/PendingQuoteConfirmModal";
import AssignTicketModal from "./components/AssignTicketModal";
import SuccessToast from "./components/SuccessToast";
import { PRINT_MACH_OPERATION, COLOR_DEV_OPERATION, REWORK_OPERATION } from "./config/printers";
import { isFeatureEnabled, FEATURES } from "./config/features";

// Admin email for feedback dashboard access
const FEEDBACK_ADMIN_EMAIL = "mark.herman@3dcolor.com";

// localStorage key for persisting work center selection
const WORK_CENTER_STORAGE_KEY = "3dcolor-selected-workcenter";

export default function WireframeApp() {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);

  // Work center - loaded from localStorage, persists across sessions
  const [allowedWorkCenters, setAllowedWorkCenters] = useState([]);
  const [selectedWorkCenter, setSelectedWorkCenter] = useState(() => {
    return localStorage.getItem(WORK_CENTER_STORAGE_KEY) || null;
  });

  // PM Dashboard access
  const [isPM, setIsPM] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasStagingAccess, setHasStagingAccess] = useState(false);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);

  // Detect if we're on staging environment
  const isStaging = typeof window !== 'undefined' && window.location.pathname.startsWith('/staging');

  // Primary navigation view state for new header
  const [activeView, setActiveView] = useState('work');

  const [showPMDashboard, setShowPMDashboard] = useState(false);
  const [showPADashboard, setShowPADashboard] = useState(false);
  const [showWorkCenterSchedule, setShowWorkCenterSchedule] = useState(false);
  const [showProjectSchedule, setShowProjectSchedule] = useState(false);
  const [showResourceLoad, setShowResourceLoad] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackDashboard, setShowFeedbackDashboard] = useState(false);
  const [showReportsDashboard, setShowReportsDashboard] = useState(false);
  const [showThroughputReport, setShowThroughputReport] = useState(false);
  const [showPerformanceReport, setShowPerformanceReport] = useState(false);
  const [showTeamView, setShowTeamView] = useState(false);
  const [showPMCommandCenter, setShowPMCommandCenter] = useState(false);
  const [showPMInterface, setShowPMInterface] = useState(false);
  const [showWorkOrderDemo, setShowWorkOrderDemo] = useState(false);
  const [showWorkOrderGenerator, setShowWorkOrderGenerator] = useState(false);
  const [showOtherModulesMenu, setShowOtherModulesMenu] = useState(false);
  const [showOrderView, setShowOrderView] = useState(false);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [showAdminTickets, setShowAdminTickets] = useState(false);
  const [showProductionSchedule, setShowProductionSchedule] = useState(false);
  const [showUnifiedBoard, setShowUnifiedBoard] = useState(false);
  const [showDataQuality, setShowDataQuality] = useState(false);
  const [showNotificationAdmin, setShowNotificationAdmin] = useState(false);
  const [showWorkInstructions, setShowWorkInstructions] = useState(false);
  const [showProductionFloorView, setShowProductionFloorView] = useState(false);
  const [showProjectProductionView, setShowProjectProductionView] = useState(false);
  const [showProductionView, setShowProductionView] = useState(false);
  const [showAdminUserManagement, setShowAdminUserManagement] = useState(false);
  const [showPAQueue, setShowPAQueue] = useState(false);
  const [showAssemblyQueue, setShowAssemblyQueue] = useState(false);
  const [showShippingQueue, setShowShippingQueue] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null); // For Project Detail Page
  const [showReworkModal, setShowReworkModal] = useState(false);

  // Quick Rework Modal - v2.1 fast issue reporting
  const [quickReworkModal, setQuickReworkModal] = useState({
    open: false,
    context: null, // { quoteNumber, orderNumber, jobNumber, projectName, workCenter }
  });

  // Reprint/Remake Modal - Unified rework reporting
  const [reprintRemakeModal, setReprintRemakeModal] = useState({
    open: false,
    preselectedQuote: null,
    preselectedJobs: null,
    workCenter: null,
  });

  // Track when backend token is ready (to avoid race condition on first login)
  const [backendTokenReady, setBackendTokenReady] = useState(
    () => !!sessionStorage.getItem("msal.backendToken")
  );

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [allQuotes, setAllQuotes] = useState([]); // For MyPinnedQuotes enrichment

  const [selectedRoutingStep, setSelectedRoutingStep] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState(null);

  // Active time sessions (from backend)
  const [activeSessions, setActiveSessions] = useState({});

  // Single-job complete modal
  const [completeModal, setCompleteModal] = useState({
    open: false,
    job: null,
    autoQuantity: false,
  });

  // Multi-job complete modal
  const [multiCompleteModal, setMultiCompleteModal] = useState({
    open: false,
    jobs: [],
    autoQuantity: false,
  });

  // Multi-select: which jobs are checked in JobTracker
  const [selectedJobIds, setSelectedJobIds] = useState([]);

  // Operation mapping (PA/PM/autoQuantity/unattended)
  const [operationMapping, setOperationMapping] = useState({});

  // Printer selection modal for PRINT-MACH operations
  const [printerModal, setPrinterModal] = useState({
    open: false,
    job: null,
    jobs: [], // For multi-job starts
    isMulti: false,
    reprintType: null, // "additional" | "reprint" | null (null = first print)
    reprintReason: null, // "internal_error" | "customer_request" | null
  });

  // Rework type modal for PA users doing REWORK operations
  const [reworkModal, setReworkModal] = useState({
    open: false,
    job: null,
    jobs: [], // For multi-job starts
    isMulti: false,
  });

  // Rework cause modal - appears for ALL REWORK/REVISION operations
  const [reworkCauseModal, setReworkCauseModal] = useState({
    open: false,
    job: null,
    jobs: [], // For multi-job starts
    isMulti: false,
    operationType: 'REWORK', // 'REWORK' or 'REVISION'
  });

  // Reprint modal for jobs that were previously printed
  const [reprintModal, setReprintModal] = useState({
    open: false,
    job: null,
    jobs: [],
    isMulti: false,
    printCount: 0,
  });

  // PM instant-complete confirmation modal
  const [pmCompleteModal, setPmCompleteModal] = useState({
    open: false,
    job: null,
  });

  // Pending Quote Mode - for tracking time before orders exist
  const [pendingQuoteMode, setPendingQuoteMode] = useState(false);
  const [pendingQuoteStarting, setPendingQuoteStarting] = useState(false);
  const [pendingQuoteConfirmModal, setPendingQuoteConfirmModal] = useState(false);

  // Pending Quote Complete Modal - shown when completing a pending quote ticket
  const [pendingQuoteCompleteModal, setPendingQuoteCompleteModal] = useState({
    open: false,
    ticket: null,
  });

  // Success toast notification
  const [successToast, setSuccessToast] = useState({ show: false, message: "" });

  // Pending tickets ready for assignment (quotes that now have orders)
  const [pendingTicketsReady, setPendingTicketsReady] = useState(0);
  const [pendingTicketsData, setPendingTicketsData] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // WebSocket connection indicator
  const [wsConnected, setWsConnected] = useState(false);

  // Pinned jobs hook for PA employees
  const { addPinnedJob } = usePinnedJobs();

  // WebSocket session event handler - reload active sessions on any change
  const handleSessionEvent = useCallback((data) => {
    console.log("📡 WebSocket session event:", data.event, data.session?.jobId);
    // Trigger a session reload on any session event
    window.dispatchEvent(new CustomEvent("time-updated"));
  }, []);

  // WebSocket sync event handler
  const handleSyncEvent = useCallback((data) => {
    console.log("📡 WebSocket sync event:", data.success ? "success" : "failed", data.count);
  }, []);

  // Initialize WebSocket connection
  const { isConnected: wsIsConnected } = useWebSocket({
    onSessionEvent: handleSessionEvent,
    onSyncEvent: handleSyncEvent,
    enabled: true,
  });

  // Update connection state
  useEffect(() => {
    setWsConnected(wsIsConnected);
  }, [wsIsConnected]);

  // --------------------------
  // DETECT iOS/Safari for special handling
  // --------------------------
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const usePopupLogin = isIOS || isSafari;

  // --------------------------
  // LOAD TOKENS
  // --------------------------
  useEffect(() => {
    async function loadTokens() {
      // Don't try to load tokens if MSAL is in the middle of an interaction
      if (inProgress !== InteractionStatus.None) {
        console.log("MSAL interaction in progress, waiting...", inProgress);
        return;
      }

      if (accounts.length === 0) return;
      const account = accounts[0];
      setUser(account);
      // Set user context for Sentry
      Sentry.setUser({
        id: account.localAccountId,
        username: account.username,
        email: account.username,
      });

      // Check for redirect loop protection
      const redirectAttempts = parseInt(sessionStorage.getItem("msal.redirectAttempts") || "0", 10);
      const lastRedirectTime = parseInt(sessionStorage.getItem("msal.lastRedirectTime") || "0", 10);
      const now = Date.now();

      // Reset redirect counter if it's been more than 30 seconds since last redirect
      if (now - lastRedirectTime > 30000) {
        sessionStorage.removeItem("msal.redirectAttempts");
        sessionStorage.removeItem("msal.lastRedirectTime");
      }

      // If we've redirected more than 3 times in 30 seconds, there's a loop - log out
      if (redirectAttempts >= 3) {
        console.error("Redirect loop detected! Logging out to break the cycle.");
        sessionStorage.removeItem("msal.redirectAttempts");
        sessionStorage.removeItem("msal.lastRedirectTime");
        sessionStorage.removeItem("msal.graphToken");
        sessionStorage.removeItem("msal.backendToken");
        // Clear all MSAL-related storage to fully reset
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('msal.') || key.includes('login.windows.net')) {
            localStorage.removeItem(key);
          }
        });
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('msal.') || key.includes('login.windows.net')) {
            sessionStorage.removeItem(key);
          }
        });
        // Clear MSAL cache and redirect to login
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + (window.location.pathname.startsWith('/tracker') ? '/tracker/' : '/'),
        });
        return;
      }

      // Helper function to handle re-authentication when tokens expire
      const handleInteractionRequired = async (scopes) => {
        console.warn(`Session expired, redirecting to login...`);
        // Track redirect attempts to prevent loops
        sessionStorage.setItem("msal.redirectAttempts", String(redirectAttempts + 1));
        sessionStorage.setItem("msal.lastRedirectTime", String(now));
        // Clear stale tokens before login
        sessionStorage.removeItem("msal.graphToken");
        sessionStorage.removeItem("msal.backendToken");

        // Always use redirect (not popup) for re-auth.
        // loginPopup on iOS/Safari is blocked by Safari because MSAL v4 does
        // async PKCE setup before window.open() — too far from the user gesture.
        // The popup→redirect fallback then corrupts MSAL's interaction state,
        // causing AADSTS900561 at login.microsoftonline.com.
        await instance.loginRedirect({ scopes, account });
      };

      try {
        const graphToken = await instance.acquireTokenSilent({
          scopes: ["user.read"],
          account,
        });
        sessionStorage.setItem("msal.graphToken", graphToken.accessToken);
      } catch (graphError) {
        // If graph token fails due to expired refresh token, login
        if (graphError instanceof InteractionRequiredAuthError) {
          await handleInteractionRequired(["user.read"]);
          return;
        }
        console.error("Graph token error:", graphError);
      }

      try {
        const apiToken = await instance.acquireTokenSilent({
          scopes: backendApiScopes,
          account,
        });
        sessionStorage.setItem("msal.backendToken", apiToken.accessToken);
        setAuthTokenProvider(() => sessionStorage.getItem("msal.backendToken"));
        setBackendTokenReady(true);
        // Clear redirect tracking on successful token acquisition
        sessionStorage.removeItem("msal.redirectAttempts");
        sessionStorage.removeItem("msal.lastRedirectTime");

        // Establish backend session (sets httpOnly cookie for long-lived auth)
        // This allows users to stay logged in even when MSAL tokens expire
        try {
          await fetch("/api/auth/session", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${apiToken.accessToken}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          });
          console.log("Backend session established");
        } catch (sessionErr) {
          // Non-fatal: user can still use Bearer token
          console.warn("Failed to establish backend session:", sessionErr);
        }
      } catch (e) {
        // If backend token fails due to expired refresh token, login
        if (e instanceof InteractionRequiredAuthError) {
          await handleInteractionRequired(backendApiScopes);
          return;
        }
        console.error("Failed to get Backend API token", e);
      }
    }

    loadTokens();
  }, [accounts, instance, inProgress, usePopupLogin]);

  // --------------------------
  // LOAD ALLOWED WORK CENTERS & PM STATUS
  // --------------------------
  useEffect(() => {
    async function loadUserInfo() {
      if (!backendTokenReady) return;

      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) return;

      try {
        const res = await fetch(apiUrl("/me"), {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const data = await res.json();
        console.log("📧 /api/me response:", data);
        const wcs = Array.isArray(data.workCenters) ? data.workCenters : [];
        setAllowedWorkCenters(wcs);
        setIsPM(data.isPM === true);
        setIsAdmin(data.isAdmin === true);
        setHasStagingAccess(data.hasStagingAccess === true);
        setUserInfoLoaded(true);

        // Validate that saved work center is still allowed
        const saved = localStorage.getItem(WORK_CENTER_STORAGE_KEY);
        if (saved && !wcs.includes(saved)) {
          // User no longer has access to this work center
          localStorage.removeItem(WORK_CENTER_STORAGE_KEY);
          setSelectedWorkCenter(null);
        }

        // If no saved work center, use the user's default (if they have one and it's allowed)
        if (!saved && data.defaultWorkCenter && wcs.includes(data.defaultWorkCenter)) {
          console.log("📍 Setting default work center:", data.defaultWorkCenter);
          localStorage.setItem(WORK_CENTER_STORAGE_KEY, data.defaultWorkCenter);
          setSelectedWorkCenter(data.defaultWorkCenter);
        }
      } catch (err) {
        console.error("Failed to load user info:", err);
        setUserInfoLoaded(true); // Still mark as loaded even on error
      }
    }

    loadUserInfo();
  }, [backendTokenReady]);

  // --------------------------
  // PERSIST WORK CENTER SELECTION
  // --------------------------
  const handleWorkCenterSelect = async (wc) => {
    if (wc) {
      localStorage.setItem(WORK_CENTER_STORAGE_KEY, wc);
    } else {
      localStorage.removeItem(WORK_CENTER_STORAGE_KEY);
    }
    setSelectedWorkCenter(wc);
    // Reset operation and job when work center changes
    // Keep quote and order selections so user doesn't have to re-select them
    setSelectedOperation(null);
    setSelectedJob(null);

    // If clearing work center, re-fetch allowed work centers to ensure they're loaded
    if (!wc) {
      const token = sessionStorage.getItem("msal.backendToken");
      if (token) {
        try {
          const res = await fetch(apiUrl("/me"), {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
          });
          const data = await res.json();
          const wcs = Array.isArray(data.workCenters) ? data.workCenters : [];
          setAllowedWorkCenters(wcs);
        } catch (err) {
          console.error("Failed to reload work centers:", err);
        }
      }
    }
  };

  // --------------------------
  // LOAD OPERATION MAPPING
  // --------------------------
  useEffect(() => {
    async function loadMapping() {
      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) return;

      try {
        const res = await fetch("/api/operations/mapping", {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const data = await res.json();
        const rawMapping = data.mapping || {};

        // Transform from operationNumber keys to operationCode keys
        // so we can look up by operationCode (e.g., "PROOF-PDF")
        const byCode = {};
        for (const opNum of Object.keys(rawMapping)) {
          const entry = rawMapping[opNum];
          if (entry.operationCode) {
            byCode[entry.operationCode] = entry;
          }
        }
        setOperationMapping(byCode);
      } catch (err) {
        console.error("Failed to load operation mapping:", err);
      }
    }

    loadMapping();
  }, []);

  function isAutoQuantityOperation(operationCode) {
    if (!operationCode) return false;
    const meta = operationMapping[operationCode];
    return meta?.autoQuantity === true;
  }

  // --------------------------
  // REAL-TIME CLOCK
  // --------------------------
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  function formatCurrentTime(date) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }

  // --------------------------
  // LOAD JOBS WHEN ORDER CHANGES
  // --------------------------
  useEffect(() => {
    async function loadJobs() {
      if (!selectedOrder?.orderNumber) {
        setJobs([]);
        return;
      }

      try {
        const token = sessionStorage.getItem("msal.backendToken");
        const res = await fetch(
          `/api/orders/${selectedOrder.orderNumber}/jobs`,
          { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
        );
        const data = await res.json();
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
      } catch {
        setJobs([]);
      }
    }
    loadJobs();
  }, [selectedOrder]);

  // Clear order selection when quote changes
  useEffect(() => {
    // Clear the order when quote changes (orders are quote-specific)
    setSelectedOrder(null);
  }, [selectedQuote?.quoteNumber]);

  // Clear job selection + multi-selection when order or operation changes
  useEffect(() => {
    setSelectedJob(null);
    setSelectedRoutingStep(null);
    setSelectedJobIds([]);
  }, [selectedOrder]);//, selectedOperation]);

  // --------------------------
  // LOAD ROUTING WHEN JOB CHANGES
  // --------------------------
  useEffect(() => {
    async function loadRouting() {
      if (!selectedJob?.jobNumber) {
        setRouting([]);
        setRoutingError(null);
        setRoutingLoading(false);
        return;
      }

      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) {
        setRouting([]);
        setRoutingError("Missing backend token");
        setRoutingLoading(false);
        return;
      }

      setRoutingLoading(true);
      setRoutingError(null);

      try {
        const res = await fetch(
          `/api/jobs/${selectedJob.jobNumber}/routing`,
          { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
        );
        const data = await res.json();
        setRouting(Array.isArray(data?.routing) ? data.routing : []);
      } catch {
        setRouting([]);
        setRoutingError("Error loading routing");
      } finally {
        setRoutingLoading(false);
      }
    }
    loadRouting();
  }, [selectedJob]);

  // --------------------------
  // LOAD ACTIVE SESSIONS
  // --------------------------
  useEffect(() => {
    async function loadActiveSessions() {
      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) {
        setActiveSessions({});
        return;
      }

      try {
        const res = await fetch("/api/me/time/active", {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const data = await res.json();
        setActiveSessions(data.sessions || {});
      } catch (err) {
        console.error("Error loading active sessions:", err);
        setActiveSessions({});
      }
    }

    loadActiveSessions();

    function handleTimeUpdated() {
      loadActiveSessions();
    }

    window.addEventListener("time-updated", handleTimeUpdated);

    // Poll for session updates every 5 seconds to catch changes from other apps
    const pollInterval = setInterval(loadActiveSessions, 5000);

    return () => {
      window.removeEventListener("time-updated", handleTimeUpdated);
      clearInterval(pollInterval);
    };
  }, []);

  // --------------------------
  // LOAD PENDING TICKETS (for assignment notification)
  // --------------------------
  const loadPendingTickets = useCallback(async () => {
    const token = sessionStorage.getItem("msal.backendToken");
    if (!token) return;

    try {
      const res = await fetch("/api/me/pending-tickets", {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const data = await res.json();
      setPendingTicketsReady(data.readyForAssignmentCount || 0);
      setPendingTicketsData(data.tickets || []);
    } catch (err) {
      console.error("Error loading pending tickets:", err);
    }
  }, []);

  useEffect(() => {
    loadPendingTickets();

    // Also reload when time-updated fires (e.g., after completing a pending quote)
    window.addEventListener("time-updated", loadPendingTickets);

    // Poll every 60 seconds to catch quotes that get orders
    const pollInterval = setInterval(loadPendingTickets, 60000);

    return () => {
      window.removeEventListener("time-updated", loadPendingTickets);
      clearInterval(pollInterval);
    };
  }, [loadPendingTickets]);

  // --------------------------
  // AUTO-PROMPT: Show assign modal when user selects a quote with pending tickets
  // --------------------------
  const dismissedPendingQuotesRef = useRef(new Set());

  const handleQuoteSelect = useCallback(async (quote) => {
    setSelectedQuote(quote);

    if (!quote?.quoteNumber) return;

    const quoteNum = String(quote.quoteNumber);

    // Don't re-prompt if user already dismissed this quote's modal
    if (dismissedPendingQuotesRef.current.has(quoteNum)) return;

    // Fetch fresh pending tickets and check for matches
    const token = sessionStorage.getItem("msal.backendToken");
    if (!token) return;

    try {
      const res = await fetch("/api/me/pending-tickets", {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const data = await res.json();
      const tickets = data.tickets || [];

      setPendingTicketsReady(data.readyForAssignmentCount || 0);
      setPendingTicketsData(tickets);

      const matchingTickets = tickets.filter(
        (t) => String(t.quoteNumber) === quoteNum && t.readyForAssignment
      );

      console.log("🔍 Quote selected:", quoteNum, "matching pending tickets:", matchingTickets.length);

      if (matchingTickets.length > 0) {
        setShowAssignModal(true);
      }
    } catch (err) {
      console.error("Error checking pending tickets on quote select:", err);
    }
  }, []);

  // --------------------------
  // SELECTION HANDLERS
  // --------------------------
  const handleRoutingStepSelect = (step) => {
    setSelectedRoutingStep(step);

    setSelectedOperation({
      operation: step.operation || step.operationCode,
      operationName: step.operation || step.operationCode,
      operationCode: step.operationCode,
      workCenter: step.workCenter,
      numberStep: step.stepNumber,
    });
  };

  const handleOperationSelect = (op) => {
    if (!op) {
      setSelectedOperation(null);
      setSelectedRoutingStep(null);
      return;
    }

    setSelectedOperation({
      operation: op.operation,
      operationName: op.operationName || op.operation || op.operationCode,
      operationCode: op.operationCode || op.code || op.operation,
      workCenter: op.workCenter,
      numberStep: op.numberStep,
    });

    setSelectedRoutingStep(null);
  };

  const handleJobClick = (job) => {
    if (selectedJob?.id === job.id) {
      setSelectedJob(null);
      setSelectedRoutingStep(null);
    } else {
      setSelectedJob(job);
      setSelectedRoutingStep(null);
    }
  };

  // --------------------------
  // TIME ENGINE HELPERS
  // --------------------------
  const ensureToken = () => {
    const token = sessionStorage.getItem("msal.backendToken");
    if (!token) {
      alert("No backend token available. Please sign in again.");
      return null;
    }
    return token;
  };

  // Show confirmation modal before starting pending quote
  const handleStartPendingQuoteClick = () => {
    if (!selectedQuote) {
      alert("Please select a quote first.");
      return;
    }
    if (!selectedOperation) {
      alert("Please select an operation first.");
      return;
    }
    setPendingQuoteConfirmModal(true);
  };

  // Actually start time tracking for a pending quote (called after confirmation)
  const handleConfirmStartPendingQuote = async () => {
    const token = ensureToken();
    if (!token) return;

    setPendingQuoteStarting(true);
    try {
      const res = await fetch(`/api/quotes/${selectedQuote.quoteNumber}/time/start`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operationCode: selectedOperation.operationCode,
          projectName: selectedQuote.user_Text4 || `Quote #${selectedQuote.quoteNumber}`,
          workCenter: selectedOperation.workCenter || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to start time tracking");
      }

      const data = await res.json();
      console.log("✅ Started pending quote time:", data);

      // Close modal, exit pending quote mode, and reload sessions
      setPendingQuoteConfirmModal(false);
      setPendingQuoteMode(false);
      window.dispatchEvent(new CustomEvent("time-updated"));
    } catch (err) {
      console.error("Error starting pending quote time:", err);
      alert(err.message || "Failed to start time tracking");
    } finally {
      setPendingQuoteStarting(false);
    }
  };

  // handleStartJob - starts either a single job OR multiple selected jobs as a group
  // If multiple checkboxes are selected, clicking Start on any job starts ALL selected jobs as a group
  const handleStartJob = async (job) => {
    if (!selectedOperation) {
      alert("Please select an operation before starting a job.");
      return;
    }

    if (!selectedOperation.operationCode) {
      alert(
        "The selected operation does not have a valid operation code. Please pick a routing step or a valid operation."
      );
      return;
    }

    // Check if multiple jobs are selected - if so, start them all as a group
    // Only include selected jobs that don't already have active sessions
    const selectedWithoutActive = selectedJobIds.filter((id) => {
      const session = activeSessions[id];
      return !session || (session.state !== "running" && session.state !== "paused");
    });

    // Debug logging
    console.log('handleStartJob - selectedJobIds:', selectedJobIds);
    console.log('handleStartJob - selectedWithoutActive:', selectedWithoutActive);
    console.log('handleStartJob - clicked job.id:', job.id);

    // If multiple jobs are selected, start them all as a group
    // Otherwise, just start the clicked job
    let jobsToStart;
    if (selectedWithoutActive.length > 1) {
      // Multiple jobs selected - start them all as a group
      jobsToStart = jobs.filter((j) => selectedWithoutActive.includes(j.id));
    } else if (selectedWithoutActive.length === 1 && selectedWithoutActive.includes(job.id)) {
      // Only the clicked job is selected - start just that one
      jobsToStart = [job];
    } else {
      // No selection or clicked job not in selection - start just the clicked job
      jobsToStart = [job];
    }

    console.log('handleStartJob - jobsToStart:', jobsToStart.length, jobsToStart.map(j => j.id));

    // Determine if this is a multi-job start
    const isMultiStart = jobsToStart.length > 1;

    // Check if this is a PRINT-MACH operation - need to select printer first
    if (selectedOperation.operationCode === PRINT_MACH_OPERATION) {
      // Check if any job was previously printed (only check first job for simplicity)
      try {
        const token = ensureToken();
        if (!token) return;

        // Check print history for this job
        const historyRes = await fetch(`/api/jobs/${job.id}/print-history`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });

        if (historyRes.ok) {
          const historyData = await historyRes.json();

          if (historyData.wasPreviouslyPrinted) {
            // Show reprint modal first
            setReprintModal({
              open: true,
              job: isMultiStart ? null : job,
              jobs: isMultiStart ? jobsToStart : [],
              isMulti: isMultiStart,
              printCount: historyData.printCount,
            });
            return;
          }
        }
      } catch (err) {
        console.error("Error checking print history:", err);
        // Continue to printer selection if check fails
      }

      // No previous print, go directly to printer selection
      setPrinterModal({
        open: true,
        job: isMultiStart ? null : job,
        jobs: isMultiStart ? jobsToStart : [],
        isMulti: isMultiStart,
      });
      return;
    }

    // Check if this is a COLOR DEV operation - need to select printer first (no print history check)
    if (selectedOperation.operationCode === COLOR_DEV_OPERATION) {
      // Go directly to printer selection (no reprint check for color dev)
      setPrinterModal({
        open: true,
        job: isMultiStart ? null : job,
        jobs: isMultiStart ? jobsToStart : [],
        isMulti: isMultiStart,
      });
      return;
    }

    // Check if this is a REWORK or REVISION operation - need to capture cause first
    if (selectedOperation.operationCode === 'REWORK' || selectedOperation.operationCode === 'REVISION') {
      setReworkCauseModal({
        open: true,
        job: isMultiStart ? null : job,
        jobs: isMultiStart ? jobsToStart : [],
        isMulti: isMultiStart,
        operationType: selectedOperation.operationCode,
      });
      return;
    }

    // For non-PRINT-MACH operations, start directly
    console.log('handleStartJob - isMultiStart:', isMultiStart, 'jobsToStart.length:', jobsToStart.length);
    if (isMultiStart) {
      console.log('handleStartJob - calling startJobsWithPrinter (plural) with', jobsToStart.length, 'jobs');
      await startJobsWithPrinter(jobsToStart, null);
      // Clear selection after starting
      setSelectedJobIds([]);
    } else {
      console.log('handleStartJob - calling startJobWithPrinter (singular) for single job');
      await startJobWithPrinter(job, null);
    }
  };

  // Handler for PM instant-complete - shows confirmation modal first
  const handleInstantComplete = (job) => {
    if (!selectedOperation) {
      alert("Please select an operation first.");
      return;
    }
    // Show confirmation modal instead of directly completing
    setPmCompleteModal({ open: true, job });
  };

  // Cancel PM instant-complete modal
  const handleCancelPmComplete = () => {
    setPmCompleteModal({ open: false, job: null });
  };

  // Confirm PM instant-complete - actually calls the API
  const handleConfirmPmComplete = async () => {
    const job = pmCompleteModal.job;
    if (!job || !selectedOperation) {
      setPmCompleteModal({ open: false, job: null });
      return;
    }

    const token = ensureToken();
    if (!token) return;

    try {
      const res = await fetch(`/api/jobs/${job.id}/time/instant-complete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          operationCode: selectedOperation.operationCode,
          workCenter: selectedOperation.workCenter || "PM",
          numberStep: selectedOperation.numberStep,
          jobNumber: job.jobNumber,
          jobName: job.name,
          qty: job.qty,
          orderNumber: selectedOrder?.orderNumber || "",
          quoteNumber: selectedOrder?.quoteNumber || null,
          projectName: selectedOrder?.projectName || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("Instant-complete error:", data);
        alert(data.error || "Failed to mark job as complete.");
        return;
      }

      console.log("Instant-complete success:", data);

      // Notify other components (this triggers handleTimeUpdated which refreshes sessions)
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Instant-complete error:", err);
      alert("Failed to mark job as complete.");
    } finally {
      // Close the modal
      setPmCompleteModal({ open: false, job: null });
    }
  };

  // Helper to start multiple jobs with optional printer
  const startJobsWithPrinter = async (jobsToStart, printer, reprintType = null, reprintReason = null, reworkCause = null, reworkNotes = null) => {
    const token = ensureToken();
    if (!token) return;
    if (!jobsToStart || jobsToStart.length === 0) return;

    console.log('startJobsWithPrinter called with', jobsToStart.length, 'jobs:', jobsToStart.map(j => j.id));

    // Get rework data from reworkModal if not passed directly (for PA rework flow)
    const finalReworkCause = reworkCause ?? reworkModal.reworkCause ?? null;
    const finalReworkNotes = reworkNotes ?? reworkModal.reworkNotes ?? null;

    // Generate group ID for multi-job starts
    let sessionGroupId = null;
    if (jobsToStart.length > 1) {
      if (window.crypto && window.crypto.randomUUID) {
        sessionGroupId = window.crypto.randomUUID();
      } else {
        sessionGroupId = `grp-${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`;
      }
      console.log('Generated sessionGroupId:', sessionGroupId);
    }

    try {
      await Promise.all(
        jobsToStart.map(async (jb) => {
          // Fetch routing for this job to get the correct stepNumber and workCenter
          let routingStepNumber = null;
          let routingWorkCenter = null;

          try {
            const routingRes = await fetch(
              `/api/jobs/${jb.jobNumber}/routing`,
              { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
            );
            const routingData = await routingRes.json();
            const routingSteps = Array.isArray(routingData?.routing)
              ? routingData.routing
              : [];

            // Find the routing step that matches the selected operation
            const matchingStep = routingSteps.find(
              (step) => step.operationCode === selectedOperation.operationCode
            );

            if (matchingStep) {
              routingStepNumber = matchingStep.stepNumber;
              routingWorkCenter = matchingStep.workCenterId ?? matchingStep.workCenter;
            }
          } catch (routingErr) {
            console.error(`Error fetching routing for job ${jb.jobNumber}:`, routingErr);
          }

          const requestBody = {
            operationCode: selectedOperation.operationCode,
            numberStep: routingStepNumber,
            workCenter: routingWorkCenter ?? selectedWorkCenter ?? null,
            jobNumber: jb.jobNumber,
            jobName: jb.name,
            productCode: jb.productCode ?? null,
            qty:
              typeof jb.qty === "number" && !Number.isNaN(jb.qty)
                ? jb.qty
                : null,
            orderNumber: selectedOrder?.orderNumber ?? "",
            quoteNumber: selectedOrder?.quoteNumber ?? null,
            projectName: selectedOrder?.projectName ?? null,
            sessionGroupId: sessionGroupId || undefined,
            printer: printer ? { id: printer.id, name: printer.name } : null,
            reprintType: reprintType,
            reprintReason: reprintReason,
            reworkCause: finalReworkCause,
            reworkNotes: finalReworkNotes,
          };
          console.log(`Starting job ${jb.id} with sessionGroupId:`, sessionGroupId);

          const res = await fetch(`/api/jobs/${jb.id}/time/start`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(requestBody),
          });

          if (!res.ok) {
            console.error("Failed to start job", jb.id);
            throw new Error("Failed to start one of the jobs.");
          }
        })
      );

      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error starting job(s):", err);
      alert("Error starting job(s). Check console for details.");
    }
  };

  // Actually start the job(s) with optional printer selection
  const startJobWithPrinter = async (job, printer, reprintType = null, reprintReason = null, reworkCause = null, reworkNotes = null) => {
    const token = ensureToken();
    if (!token) return;

    // Only create a group session when explicitly starting multiple jobs via printerModal.isMulti
    // Single job clicks should always create individual sessions, even if multiple checkboxes are selected
    const isMultiGroup = printerModal.isMulti && printerModal.jobs && printerModal.jobs.length > 1;

    let jobsToStart = job ? [job] : printerModal.jobs;
    let sessionGroupId = null;

    // Get reprint data from printerModal if not passed directly
    const finalReprintType = reprintType ?? printerModal.reprintType ?? null;
    const finalReprintReason = reprintReason ?? printerModal.reprintReason ?? null;

    // Get rework data from reworkModal if not passed directly (for PA rework flow)
    const finalReworkCause = reworkCause ?? reworkModal.reworkCause ?? null;
    const finalReworkNotes = reworkNotes ?? reworkModal.reworkNotes ?? null;

    if (isMultiGroup) {
      // Only use multi-select jobs when explicitly triggered via multi-start action
      jobsToStart = printerModal.jobs;

      if (window.crypto && window.crypto.randomUUID) {
        sessionGroupId = window.crypto.randomUUID();
      } else {
        sessionGroupId = `grp-${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`;
      }
    }

    try {
      await Promise.all(
        jobsToStart.map(async (jb) => {
          // Fetch routing for this job to get the correct stepNumber and workCenter
          let routingStepNumber = null;
          let routingWorkCenter = null;

          try {
            const routingRes = await fetch(
              `/api/jobs/${jb.jobNumber}/routing`,
              { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
            );
            const routingData = await routingRes.json();
            const routingSteps = Array.isArray(routingData?.routing)
              ? routingData.routing
              : [];

            // Find the routing step that matches the selected operation
            const matchingStep = routingSteps.find(
              (step) => step.operationCode === selectedOperation.operationCode
            );

            if (matchingStep) {
              routingStepNumber = matchingStep.stepNumber;
              // Use workCenterId if available, otherwise fall back to workCenter
              routingWorkCenter = matchingStep.workCenterId ?? matchingStep.workCenter;
              console.log(`Found routing step for job ${jb.jobNumber}:`, matchingStep);
              console.log(`Using stepNumber: ${routingStepNumber}, workCenter: ${routingWorkCenter}`);
            } else {
              console.warn(
                `No routing step found for operation ${selectedOperation.operationCode} on job ${jb.jobNumber}`
              );
            }
          } catch (routingErr) {
            console.error(`Error fetching routing for job ${jb.jobNumber}:`, routingErr);
          }

          const res = await fetch(`/api/jobs/${jb.id}/time/start`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              operationCode: selectedOperation.operationCode,
              numberStep: routingStepNumber,
              workCenter: routingWorkCenter ?? selectedWorkCenter ?? null,
              jobNumber: jb.jobNumber,
              jobName: jb.name,
              productCode: jb.productCode ?? null,
              qty:
                typeof jb.qty === "number" && !Number.isNaN(jb.qty)
                  ? jb.qty
                  : null,
              orderNumber: selectedOrder?.orderNumber ?? "",
              quoteNumber: selectedOrder?.quoteNumber ?? null,
              projectName: selectedOrder?.projectName ?? null,
              sessionGroupId: sessionGroupId || undefined,
              // Include printer info for PRINT-MACH operations
              printer: printer ? { id: printer.id, name: printer.name } : null,
              // Include reprint tracking for reprinted PRINT-MACH jobs
              reprintType: finalReprintType,
              reprintReason: finalReprintReason,
              // Include rework cause tracking for REWORK/REVISION operations
              reworkCause: finalReworkCause,
              reworkNotes: finalReworkNotes,
            }),
          });

          if (!res.ok) {
            console.error("Failed to start job", jb.id);
            throw new Error("Failed to start one of the jobs.");
          }
        })
      );

      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error starting job(s):", err);
      alert("Error starting job(s). Check console for details.");
    }
  };

  // Handler for printer selection confirmation
  const handlePrinterSelect = async (printer) => {
    // Capture modal state BEFORE clearing (important for multi-job)
    const job = printerModal.job;
    const jobsToStart = printerModal.isMulti ? printerModal.jobs : (job ? [job] : []);
    const isMulti = printerModal.isMulti;

    setPrinterModal({ open: false, job: null, jobs: [], isMulti: false, reprintType: null, reprintReason: null });

    // Use captured jobs for multi-job, single job otherwise
    if (isMulti || jobsToStart.length > 1) {
      await startJobsWithPrinter(jobsToStart, printer);
      // Clear selection after starting multi-job
      setSelectedJobIds([]);
    } else if (job) {
      await startJobWithPrinter(job, printer);
    }
  };

  const handlePrinterCancel = () => {
    setPrinterModal({ open: false, job: null, jobs: [], isMulti: false, reprintType: null, reprintReason: null });
  };

  // Handler for rework type selection (printing vs other)
  const handleReworkSelect = async (selection) => {
    const job = reworkModal.job;
    const jobsToStart = reworkModal.isMulti ? reworkModal.jobs : (job ? [job] : []);
    const isMulti = reworkModal.isMulti;
    setReworkModal({ open: false, job: null, jobs: [], isMulti: false });

    // If printing was selected, start with the selected printer
    // If other was selected, start without printer
    const printer = selection.type === "printing" ? selection.printer : null;

    // Start the job(s) with the printer (or null for other rework)
    await startJobsWithPrinter(jobsToStart, printer);

    // Clear selection after starting multi-job
    if (isMulti || jobsToStart.length > 1) {
      setSelectedJobIds([]);
    }
  };

  const handleReworkCancel = () => {
    setReworkModal({ open: false, job: null, jobs: [], isMulti: false, reworkCause: null, reworkNotes: null });
  };

  // Handler for rework cause modal submission
  const handleReworkCauseSubmit = async ({ cause, notes }) => {
    const job = reworkCauseModal.job;
    const jobsToStart = reworkCauseModal.isMulti ? reworkCauseModal.jobs : (job ? [job] : []);
    const isMulti = reworkCauseModal.isMulti;
    const operationType = reworkCauseModal.operationType;

    setReworkCauseModal({ open: false, job: null, jobs: [], isMulti: false, operationType: 'REWORK' });

    // For REWORK operation on PA work center, need to ask printing vs other
    if (operationType === 'REWORK' && selectedWorkCenter === 'PA') {
      setReworkModal({
        open: true,
        job: isMulti ? null : job,
        jobs: isMulti ? jobsToStart : [],
        isMulti: isMulti,
        reworkCause: cause,
        reworkNotes: notes,
      });
      return;
    }

    // For all other cases, start directly with the cause info
    if (isMulti || jobsToStart.length > 1) {
      await startJobsWithPrinter(jobsToStart, null, null, null, cause, notes);
      setSelectedJobIds([]);
    } else if (job) {
      await startJobWithPrinter(job, null, null, null, cause, notes);
    }
  };

  const handleReworkCauseCancel = () => {
    setReworkCauseModal({ open: false, job: null, jobs: [], isMulti: false, operationType: 'REWORK' });
  };

  // Handler for reprint modal selection (additional order or reprint)
  const handleReprintSelect = (selection) => {
    // selection is now an object: { type: "additional" | "reprint", reason: "internal_error" | "customer_request" | null }
    // Capture modal state before clearing
    const job = reprintModal.job;
    const jobsToStart = reprintModal.isMulti ? reprintModal.jobs : (job ? [job] : []);
    const isMulti = reprintModal.isMulti;

    setReprintModal({ open: false, job: null, jobs: [], isMulti: false, printCount: 0 });

    // Log the selection for analytics
    console.log(`Print type selected: ${selection.type}${selection.reason ? ` (${selection.reason})` : ''} for job(s)`, jobsToStart.map(j => j.jobNumber));

    // Proceed to printer selection with reprint data
    if (isMulti) {
      setPrinterModal({
        open: true,
        job: null,
        jobs: jobsToStart,
        isMulti: true,
        reprintType: selection.type,
        reprintReason: selection.reason,
      });
    } else if (job) {
      setPrinterModal({
        open: true,
        job: job,
        jobs: [],
        isMulti: false,
        reprintType: selection.type,
        reprintReason: selection.reason,
      });
    }
  };

  const handleReprintCancel = () => {
    setReprintModal({ open: false, job: null, jobs: [], isMulti: false, printCount: 0 });
  };

  const handlePauseJob = async (job) => {
    const token = ensureToken();
    if (!token) return;

    try {
      const res = await fetch(`/api/jobs/${job.id}/time/pause`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!res.ok) {
        console.error("Failed to pause job");
        alert("Failed to pause job.");
        return;
      }

      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error pausing job:", err);
      alert("Error pausing job.");
    }
  };

  const handleResumeJob = async (job) => {
    const token = ensureToken();
    if (!token) return;

    try {
      const res = await fetch(`/api/jobs/${job.id}/time/resume`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!res.ok) {
        console.error("Failed to resume job");
        alert("Failed to resume job.");
        return;
      }

      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error resuming job:", err);
      alert("Error resuming job.");
    }
  };

  // --------------------------
  // COMPLETE HANDLERS
  // --------------------------

  // Single-job complete (existing behavior, with multi-select hook AND group session detection)
  const handleCompleteJob = (job) => {
    console.log("🔍 handleCompleteJob - job:", job);
    // Check if this is a pending quote ticket (job ID starts with "PENDING-")
    const isPendingQuote = job?.id?.startsWith?.("PENDING-");
    console.log("🔍 isPendingQuote:", isPendingQuote);
    if (isPendingQuote) {
      // Get the active session for this pending quote
      const session = activeSessions[job.id];
      console.log("🔍 session from activeSessions:", session);
      const ticket = {
        ...job,
        ...session,
        ticketId: session?.ticketId,
        quoteNumber: job.quoteNumber || session?.quoteNumber || job.id?.split("-")?.[1],
      };
      console.log("🔍 constructed ticket:", ticket);
      setPendingQuoteCompleteModal({
        open: true,
        ticket,
      });
      return;
    }

    const autoQty = isAutoQuantityOperation(selectedOperation?.operationCode);

    // Check if this job is part of a group session
    const jobSession = activeSessions[job.id];
    const sessionGroupId = jobSession?.sessionGroupId;

    // Find all jobs that are part of the same session group
    let groupedJobs = [];
    if (sessionGroupId) {
      groupedJobs = jobs.filter((j) => {
        const s = activeSessions[j.id];
        return s?.sessionGroupId === sessionGroupId && (s?.state === "running" || s?.state === "paused");
      });
    }

    // Also consider manual multi-selection
    const manualMulti =
      selectedJobIds &&
      selectedJobIds.length > 1 &&
      selectedOperation?.operationCode;

    // Use group session jobs if available, otherwise fall back to manual selection
    const isGroupSession = groupedJobs.length > 1;
    const isMulti = isGroupSession || manualMulti;

    if (isMulti) {
      const jobsForModal = isGroupSession
        ? groupedJobs
        : jobs.filter((j) => selectedJobIds.includes(j.id));

      setMultiCompleteModal({
        open: true,
        jobs: jobsForModal,
        autoQuantity: autoQty,
      });
    } else {
      setCompleteModal({ open: true, job, autoQuantity: autoQty });
    }
  };

  const handleConfirmComplete = async (piecesFinished) => {
    const job = completeModal.job;
    if (!job) return;

    const token = ensureToken();
    if (!token) return;

    // Save the operation code for pinning check
    const completedOperationCode = selectedOperation?.operationCode;

    try {
      const res = await fetch(`/api/jobs/${job.id}/time/complete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ piecesFinished }),
      });

      if (!res.ok) {
        console.error("Failed to complete job");
        alert("Failed to complete job.");
        return;
      }

      const syncRes = await fetch("/api/me/time/sync", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!syncRes.ok) {
        console.error("Failed to sync time tickets");
        alert("Job completed, but sync to JobBOSS may have failed.\n\nYou can retry the sync from the Session Dashboard (clock icon) using the 'Sync to JobBOSS' button.");
      }

      // Pin job if PROOF-PDF was completed (for PA employees)
      if (completedOperationCode === "PROOF-PDF" && selectedWorkCenter === "PA") {
        addPinnedJob({
          id: job.id,
          jobNumber: job.jobNumber,
          name: job.name,
          productCode: job.productCode,
          qty: job.qty,
          orderNumber: selectedOrder?.orderNumber,
          quoteNumber: selectedOrder?.quoteNumber,
          projectName: selectedOrder?.projectName,
          customer: selectedOrder?.customerCode, // API returns customerCode
          projectManager: selectedOrder?.projectManager,
        }, completedOperationCode);
      }

      window.dispatchEvent(new Event("time-updated"));
      setCompleteModal({ open: false, job: null, autoQuantity: false });
    } catch (err) {
      console.error("Error completing job:", err);
      alert("Error completing job.");
    }
  };

  const handleCancelComplete = () => {
    setCompleteModal({ open: false, job: null, autoQuantity: false });
  };

  // Multi-job complete
  const handleConfirmMultiComplete = async (result) => {
    const token = ensureToken();
    if (!token) return;

    const { jobs: modalJobs, autoQuantity } = multiCompleteModal;
    if (!modalJobs || modalJobs.length === 0) {
      setMultiCompleteModal({ open: false, jobs: [], autoQuantity: false });
      return;
    }

    // Save the operation code for pinning check
    const completedOperationCode = selectedOperation?.operationCode;

    try {
      const jobsToComplete = modalJobs;

      for (const job of jobsToComplete) {
        let piecesFinished = 0;
        let piecesScrapped = 0;

        if (autoQuantity) {
          piecesFinished = job.qty ?? 0;
          piecesScrapped = 0;
        } else if (result?.type === "table" && result.byJob?.[job.id]) {
          const row = result.byJob[job.id];
          piecesFinished = Number(row.piecesFinished || 0);
          piecesScrapped = Number(row.piecesScrapped || 0);
        }

        const res = await fetch(`/api/jobs/${job.id}/time/complete`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            piecesFinished,
            piecesScrapped,
          }),
        });

        if (!res.ok) {
          console.error("Failed to complete job", job.id);
        }

        // Pin job if PROOF-PDF was completed (for PA employees)
        if (completedOperationCode === "PROOF-PDF" && selectedWorkCenter === "PA") {
          addPinnedJob({
            id: job.id,
            jobNumber: job.jobNumber,
            name: job.name,
            productCode: job.productCode,
            qty: job.qty,
            orderNumber: selectedOrder?.orderNumber,
            quoteNumber: selectedOrder?.quoteNumber,
            projectName: selectedOrder?.projectName,
            customer: selectedOrder?.customerCode, // API returns customerCode
            projectManager: selectedOrder?.projectManager,
          }, completedOperationCode);
        }
      }

      const syncRes = await fetch("/api/me/time/sync", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!syncRes.ok) {
        console.error("Failed to sync time tickets after multi-complete");
        alert("Jobs completed, but sync to JobBOSS may have failed.\n\nYou can retry the sync from the Session Dashboard (clock icon) using the 'Sync to JobBOSS' button.");
      }

      window.dispatchEvent(new Event("time-updated"));

      setMultiCompleteModal({ open: false, jobs: [], autoQuantity: false });
      setSelectedJobIds([]);
    } catch (err) {
      console.error("Error completing multiple jobs:", err);
      alert("Error completing multiple jobs.");
    }
  };

  const handleCancelMultiComplete = () => {
    setMultiCompleteModal({ open: false, jobs: [], autoQuantity: false });
  };

  // Handle pending quote complete
  const handlePendingQuoteComplete = async ({ piecesFinished, useAutoQuantity }) => {
    const ticket = pendingQuoteCompleteModal.ticket;
    if (!ticket) return;

    console.log("🔍 handlePendingQuoteComplete - ticket:", ticket);
    console.log("🔍 Using ticket.id for jobId:", ticket.id);

    const token = ensureToken();
    if (!token) return;

    try {
      // Complete the pending quote ticket
      const res = await fetch(`/api/jobs/${ticket.id}/time/complete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          piecesFinished: piecesFinished,
          piecesScrapped: 0,
          useAutoQuantity: useAutoQuantity,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to complete ticket");
      }

      // Sync to JobBOSS (will mark as pending_assignment, not actually sync since no real job)
      await fetch("/api/me/time/sync", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      window.dispatchEvent(new Event("time-updated"));
      setPendingQuoteCompleteModal({ open: false, ticket: null });
    } catch (err) {
      console.error("Error completing pending quote ticket:", err);
      alert(err.message || "Error completing ticket");
    }
  };

  const handleCancelPendingQuoteComplete = () => {
    setPendingQuoteCompleteModal({ open: false, ticket: null });
  };

  // --------------------------
  // UI HELPERS
  // --------------------------
  const card =
    "bg-black/40 border border-white/10 rounded-lg shadow-lg p-3 flex flex-col gap-2";

  const selectorDimClass = selectedJob ? "opacity-40 pointer-events-none" : "";

  // --------------------------
  // KEYBOARD SHORTCUTS
  // --------------------------
  const { shortcuts } = useKeyboardShortcuts({
    jobs,
    selectedJob,
    selectedJobIds,
    activeSessions,
    selectedOperation,
    onJobClick: handleJobClick,
    onStartJob: handleStartJob,
    onPauseJob: handlePauseJob,
    onResumeJob: handleResumeJob,
    onCompleteJob: handleCompleteJob,
    onSelectionChange: setSelectedJobIds,
    onCloseDrawer: () => setSelectedJob(null),
    enabled: !completeModal.open && !multiCompleteModal.open && !printerModal.open && !reworkModal.open,
  });

  // --------------------------
  // BATCH OPERATIONS COUNT
  // --------------------------
  const batchSessionCount = useMemo(() => {
    // Count sessions that are part of a group (shared sessions)
    const sessionGroups = new Set();
    let groupedJobCount = 0;

    Object.values(activeSessions).forEach((session) => {
      if (session?.sessionGroupId) {
        sessionGroups.add(session.sessionGroupId);
        groupedJobCount++;
      }
    });

    return {
      groupCount: sessionGroups.size,
      jobCount: groupedJobCount,
    };
  }, [activeSessions]);

  // --------------------------
  // ACTIVE SESSION COUNT
  // --------------------------
  const activeSessionCount = useMemo(() => {
    return Object.values(activeSessions).filter(
      (s) => s?.state === "running" || s?.state === "paused"
    ).length;
  }, [activeSessions]);

  // --------------------------
  // LOGIN SCREEN
  // --------------------------
  if (!user) {
    const handleSignIn = async () => {
      // Always use redirect for the initial login — loginPopup on iOS/Safari is
      // unreliable because MSAL v4 does async PKCE setup before window.open(),
      // which breaks Safari's direct-gesture requirement and blocks the popup.
      // The catch-block fallback then calls loginRedirect() with stale partial
      // interaction state still in sessionStorage, producing AADSTS900561.
      // loginRedirect works correctly on all platforms including iOS Safari.
      await instance.loginRedirect(loginRequest);
    };

    return (
      <div className="bg-[#1a1a1a] text-white h-screen flex flex-col justify-center items-center">
        <h1 className="text-2xl mb-2">Time Collection App</h1>
        <p className="opacity-70 mb-4">Please sign in to continue</p>
        <button
          onClick={handleSignIn}
          className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700"
        >
          Sign In
        </button>
      </div>
    );
  }

  // --------------------------
  // STAGING ACCESS DENIED SCREEN
  // --------------------------
  if (isStaging && userInfoLoaded && !hasStagingAccess) {
    const handleLogout = () => {
      sessionStorage.removeItem("msal.backendToken");
      instance.logoutRedirect();
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 text-white flex flex-col items-center justify-center p-4">
        <div className="bg-slate-800/80 border border-white/10 rounded-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">Staging Access Denied</h1>
          <p className="text-gray-400 mb-6">
            You don't have access to the staging environment. Contact an administrator if you need access for testing.
          </p>
          <div className="flex gap-3 justify-center">
            <a
              href="/tracker/"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded font-medium transition-colors"
            >
              Go to Production
            </a>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded font-medium transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-500">
          Signed in as {user?.email || user?.preferred_username || user?.name}
        </p>
      </div>
    );
  }

  // User email for access control (moved up for new navigation check)
  const userEmail = user?.username?.toLowerCase() || "";

  // --------------------------
  // NEW NAVIGATION SYSTEM (Phase 2)
  // --------------------------
  // When enabled, renders the new section-based navigation shell
  // This bypasses the work center selector and all existing view state flags
  if (isFeatureEnabled(FEATURES.NEW_NAVIGATION, userEmail)) {
    const navUser = {
      name: user?.name || user?.username,
      email: userEmail,
      isAdmin,
      isPM,
    };

    const handleSignOut = () => {
      instance.logoutRedirect();
    };

    const handleSwitchAccount = async () => {
      // Clear all tokens and MSAL cache
      sessionStorage.removeItem("msal.backendToken");
      sessionStorage.removeItem("msal.graphToken");
      localStorage.removeItem(WORK_CENTER_STORAGE_KEY);

      // Clear all MSAL-related storage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('msal.') || key.includes('login.windows.net')) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('msal.') || key.includes('login.windows.net')) {
          sessionStorage.removeItem(key);
        }
      });

      // Redirect with account selection prompt
      await instance.loginRedirect({
        scopes: backendApiScopes,  // was: apiScopes (undefined reference)
        prompt: 'select_account', // Forces account picker to appear
      });
    };

    const handleShowProfile = () => {
      // Profile shows user info - for now just alert
      alert(`Profile\n\nName: ${user?.name || 'Unknown'}\nEmail: ${userEmail}\nRole: ${isAdmin ? 'Admin' : isPM ? 'PM' : 'User'}`);
    };

    const handleShowDownloads = () => {
      // Downloads - placeholder for future file download functionality
      alert('Downloads\n\nFile downloads coming soon.\n\nThis will include exported reports and data files.');
    };

    const handleShowFeedback = () => {
      setShowFeedbackModal(true);
    };

    // Open Quick Rework Modal for fast issue reporting (legacy)
    const handleOpenQuickRework = (context) => {
      setQuickReworkModal({
        open: true,
        context: context || null,
      });
    };

    const handleCloseQuickRework = () => {
      setQuickReworkModal({ open: false, context: null });
    };

    // Open unified Reprint/Remake Modal
    const handleOpenReprintRemake = (context = {}) => {
      setReprintRemakeModal({
        open: true,
        preselectedQuote: context.quote || null,
        preselectedJobs: context.jobs || null,
        workCenter: context.workCenter || null,
      });
    };

    const handleCloseReprintRemake = () => {
      setReprintRemakeModal({
        open: false,
        preselectedQuote: null,
        preselectedJobs: null,
        workCenter: null,
      });
    };

    // Handle project selection from PA View / Assembly View
    const handleSelectProjectNewNav = (project) => {
      // Store project in sessionStorage for the detail page to read
      sessionStorage.setItem('3dcolor-selected-project', JSON.stringify(project));
      // Navigate to project detail view
      window.location.hash = '#/production/project-detail';
    };

    return (
      <PinnedQuotesProvider>
        <MainNavigation
          user={navUser}
          onSignOut={handleSignOut}
          onSwitchAccount={handleSwitchAccount}
          onShowProfile={handleShowProfile}
          onShowDownloads={handleShowDownloads}
          onShowFeedback={handleShowFeedback}
          onReprintRemake={handleOpenReprintRemake}
          viewProps={{
            userEmail,
            isPM,
            isAdmin,
            token: sessionStorage.getItem("msal.backendToken"),
            employeeId: user?.id,
            employeeName: user?.name || userEmail?.split('@')[0],
            onSelectProject: handleSelectProjectNewNav,
            onReportIssue: handleOpenReprintRemake,
          }}
        />
        {/* Keep feedback modal accessible */}
        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
            userEmail={userEmail}
          />
        )}

        {/* Quick Rework Modal - v2.1 fast issue reporting */}
        <QuickReworkModal
          isOpen={quickReworkModal.open}
          onClose={handleCloseQuickRework}
          context={quickReworkModal.context}
          employeeId={user?.id || user?.localAccountId}
          employeeName={user?.name || userEmail?.split('@')[0]}
          token={sessionStorage.getItem("msal.backendToken")}
          onSuccess={() => {
            handleCloseQuickRework();
            // Could trigger a refresh of affected views here
          }}
        />

        {/* Unified Reprint/Remake Modal */}
        <ReprintRemakeModal
          isOpen={reprintRemakeModal.open}
          onClose={handleCloseReprintRemake}
          preselectedQuote={reprintRemakeModal.preselectedQuote}
          preselectedJobs={reprintRemakeModal.preselectedJobs}
          workCenter={reprintRemakeModal.workCenter}
          currentUser={{
            employeeId: user?.localAccountId || user?.id,
            displayName: user?.name || userEmail?.split('@')[0],
          }}
          token={sessionStorage.getItem("msal.backendToken")}
        />
      </PinnedQuotesProvider>
    );
  }

  // --------------------------
  // WORK CENTER SELECTION SCREEN (Legacy navigation only)
  // --------------------------
  if (!selectedWorkCenter) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white flex flex-col">
        {/* HEADER */}
        <header className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h1 className="text-xl font-semibold tracking-wide">
                3D COLOR
              </h1>
              <span className="text-xs opacity-80 uppercase">
                Time Collection App
              </span>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <NotificationBell
                userEmail={user?.email || user?.preferred_username}
                onNavigate={(viewId) => {
                  window.location.hash = `#/production/${viewId}`;
                }}
              />
              <span className="text-white/70">{user?.name || user?.username}</span>
            </div>
          </div>
        </header>

        {/* WORK CENTER SELECTOR */}
        <main className="flex-1 flex items-center justify-center">
          <WorkCenterSelector
            allowedWorkCenters={allowedWorkCenters}
            selectedWorkCenter={selectedWorkCenter}
            onSelect={handleWorkCenterSelect}
          />
        </main>
      </div>
    );
  }

  // --------------------------
  // NAVIGATION HELPERS
  // --------------------------
  const closeAllViews = () => {
    setShowWorkCenterSchedule(false);
    setShowProjectSchedule(false);
    setShowTeamView(false);
    setShowPADashboard(false);
    setShowProductionSchedule(false);
    setShowUnifiedBoard(false);
    setShowProductionView(false);
    setShowProductionFloorView(false);
    setShowProjectProductionView(false);
    setShowPMCommandCenter(false);
    setShowPMInterface(false);
    setShowReportsDashboard(false);
    setShowThroughputReport(false);
    setShowPerformanceReport(false);
    setShowAdminTickets(false);
    setShowAdminUserManagement(false);
    setShowDataQuality(false);
    setShowNotificationAdmin(false);
    setShowFeedbackDashboard(false);
    setShowWorkInstructions(false);
    setShowWorkOrderDemo(false);
    setShowWorkOrderGenerator(false);
    setShowSessionHistory(false);
    setShowOrderView(false);
    setShowPAQueue(false);
    setShowAssemblyQueue(false);
    setShowShippingQueue(false);
    setSelectedProject(null);
    setActiveView('work');
  };

  // --------------------------
  // PAGE WRAPPER - Renders any view with persistent header
  // --------------------------
  const renderPageWithHeader = (content, options = {}) => {
    const { viewId = 'work' } = options;
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white flex flex-col">
        {/* Persistent Header */}
        <div className="sticky top-0 z-50">
          <AppHeader
            user={user}
            userEmail={userEmail}
            isPM={isPM}
            isAdmin={isAdmin}
            displayName={user?.name || userEmail?.split('@')[0]}
            activeSessionCount={activeSessionCount}
            batchSessionCount={batchSessionCount}
            currentView={viewId}
            onNavigateHome={closeAllViews}
            setShowProjectSchedule={setShowProjectSchedule}
            setShowTeamView={setShowTeamView}
            setShowPADashboard={setShowPADashboard}
            setShowPAQueue={setShowPAQueue}
            setShowAssemblyQueue={setShowAssemblyQueue}
            setShowShippingQueue={setShowShippingQueue}
            setShowProductionSchedule={setShowProductionSchedule}
            setShowProductionView={setShowProductionView}
            setShowWorkOrderGenerator={setShowWorkOrderGenerator}
            setShowPMCommandCenter={setShowPMCommandCenter}
            setShowPMInterface={setShowPMInterface}
            setShowWorkOrderDemo={setShowWorkOrderDemo}
            setShowReportsDashboard={setShowReportsDashboard}
            setShowFeedbackDashboard={setShowFeedbackDashboard}
            setShowAdminTickets={setShowAdminTickets}
            setShowAdminUserManagement={setShowAdminUserManagement}
            setShowWorkInstructions={setShowWorkInstructions}
            setShowDataQuality={setShowDataQuality}
            setShowNotificationAdmin={setShowNotificationAdmin}
            setShowFeedbackModal={setShowFeedbackModal}
            onReprintRemake={handleOpenReprintRemake}
            NotificationSettings={NotificationSettings}
            isStaging={isStaging}
          />
        </div>
        {/* Page Content */}
        <div className="flex-1 flex flex-col">
          {content}
        </div>
      </div>
    );
  };

  // --------------------------
  // WORK CENTER SCHEDULE VIEW
  // --------------------------
  if (showWorkCenterSchedule && isPM) {
    return renderPageWithHeader(
      <WorkCenterSchedule
        onBackToApp={closeAllViews}
        selectedWorkCenter={selectedWorkCenter}
      />,
      { viewId: 'schedule' }
    );
  }

  // --------------------------
  // PROJECT SCHEDULE VIEW (available to all users)
  // --------------------------
  if (showProjectSchedule) {
    return renderPageWithHeader(
      <ProjectSchedule
        onBackToApp={closeAllViews}
        onNavigateToOrder={(quoteNumber, orderNumber) => {
          // Navigate back to main app with quote/order pre-selected
          closeAllViews();
          if (quoteNumber) {
            setSelectedQuote({ quoteNumber });
            // Store target order to auto-select once orders load
            if (orderNumber) {
              sessionStorage.setItem("3dcolor-pending-order", orderNumber);
            }
          }
        }}
      />,
      { viewId: 'projects' }
    );
  }

  // --------------------------
  // RESOURCE LOAD DASHBOARD VIEW (temporarily hidden)
  // --------------------------
  // if (showResourceLoad && isPM) {
  //   return (
  //     <ResourceLoadDashboard
  //       onBackToApp={() => setShowResourceLoad(false)}
  //     />
  //   );
  // }

  // --------------------------
  // PA DASHBOARD VIEW (available to all users)
  // --------------------------
  if (showPADashboard) {
    return renderPageWithHeader(
      <PADashboard
        onBackToApp={closeAllViews}
      />,
      { viewId: 'printers' }
    );
  }

  // --------------------------
  // TEAM VIEW (available to all users)
  // --------------------------
  if (showTeamView) {
    return renderPageWithHeader(
      <TeamView
        onBackToApp={closeAllViews}
        userEmail={userEmail}
      />,
      { viewId: 'team' }
    );
  }

  // --------------------------
  // PROJECT DETAIL PAGE (from All Projects, PA Queue, Assembly Queue)
  // Must be checked BEFORE view-specific checks like showUnifiedBoard
  // --------------------------
  if (selectedProject) {
    return renderPageWithHeader(
      <ProjectDetailPage
        workOrder={selectedProject}
        token={sessionStorage.getItem("msal.backendToken")}
        employeeId={user?.id}
        employeeName={user?.name || userEmail?.split('@')[0]}
        onBack={() => setSelectedProject(null)}
        onStartTimer={(wo) => {
          // TODO: Integrate with time tracking
          console.log('Start timer for:', wo);
        }}
      />,
      { viewId: 'project-detail' }
    );
  }

  // --------------------------
  // PRODUCTION SCHEDULE VIEW (beta feature)
  // --------------------------
  if (showProductionSchedule && isFeatureEnabled(FEATURES.PRODUCTION_SCHEDULE, userEmail)) {
    return renderPageWithHeader(
      <ProductionScheduleBoard
        onBackToApp={closeAllViews}
      />,
      { viewId: 'schedule' }
    );
  }

  // --------------------------
  // UNIFIED PROJECT BOARD (canonical stage view)
  // --------------------------
  if (showUnifiedBoard) {
    return renderPageWithHeader(
      <UnifiedProjectBoard
        onSelectProject={(project) => setSelectedProject(project)}
      />,
      { viewId: 'unified-board' }
    );
  }

  // --------------------------
  // SESSION HISTORY VIEW (available to all users)
  // --------------------------
  if (showSessionHistory) {
    return renderPageWithHeader(
      <div className="flex-1 bg-gray-950">
        <div className="max-w-6xl mx-auto py-4">
          <SessionDashboard onBackToTimeTracker={() => setShowSessionHistory(false)} />
        </div>
      </div>,
      { viewId: 'session-history' }
    );
  }

  // --------------------------
  // UNIFIED PRODUCTION VIEW (streamlined interface for production workers)
  // --------------------------
  if (showProductionView && isFeatureEnabled(FEATURES.PRODUCTION_FLOOR_VIEW, userEmail)) {
    return renderPageWithHeader(
      <ProductionView
        onBackToApp={closeAllViews}
        userEmail={userEmail}
      />,
      { viewId: 'production' }
    );
  }

  // --------------------------
  // PRODUCTION FLOOR VIEW (legacy - simplified interface for production workers)
  // --------------------------
  if (showProductionFloorView && isFeatureEnabled(FEATURES.PRODUCTION_FLOOR_VIEW, userEmail)) {
    return renderPageWithHeader(
      <ProductionFloorView
        onBackToApp={closeAllViews}
        userEmail={userEmail}
      />,
      { viewId: 'production' }
    );
  }

  // --------------------------
  // PROJECT PRODUCTION VIEW (legacy - project-centric view with routing context)
  // --------------------------
  if (showProjectProductionView && isFeatureEnabled(FEATURES.PRODUCTION_FLOOR_VIEW, userEmail)) {
    return renderPageWithHeader(
      <ProjectProductionView
        onBackToApp={closeAllViews}
        userEmail={userEmail}
      />,
      { viewId: 'production' }
    );
  }

  // --------------------------
  // PM WORK ORDER GENERATOR (new simplified interface)
  // --------------------------
  if (showWorkOrderGenerator && isPM) {
    return renderPageWithHeader(
      <PMWorkOrderGenerator
        onClose={closeAllViews}
        onShowOtherModules={closeAllViews}
      />,
      { viewId: 'work-orders' }
    );
  }

  // --------------------------
  // PM INTERFACE VIEW (new unified PM view)
  // --------------------------
  if (showPMInterface && isPM) {
    return renderPageWithHeader(
      <PMInterface
        onBackToApp={closeAllViews}
        onSelectProject={(project) => {
          setSelectedProject(project);
          setShowPMInterface(false);
        }}
      />,
      { viewId: 'pm-interface' }
    );
  }

  // --------------------------
  // PM COMMAND CENTER VIEW (legacy - available to PM users)
  // --------------------------
  if (showPMCommandCenter && isPM) {
    return renderPageWithHeader(
      <PMCommandCenter
        onBackToApp={closeAllViews}
      />,
      { viewId: 'pm-command' }
    );
  }

  // --------------------------
  // WORK ORDER DEMO VIEW (available to PM users)
  // --------------------------
  if (showWorkOrderDemo && isPM) {
    return renderPageWithHeader(
      <div className="flex-1 bg-gray-100">
        <PMWorkOrderDemo />
      </div>,
      { viewId: 'work-order-review' }
    );
  }

  // --------------------------
  // REPORTS DASHBOARD VIEW
  // --------------------------
  if (showReportsDashboard) {
    return renderPageWithHeader(
      <ReportsDashboard
        onBackToApp={closeAllViews}
      />,
      { viewId: 'reports' }
    );
  }

  // --------------------------
  // THROUGHPUT REPORT VIEW (Mark only)
  // --------------------------
  if (showThroughputReport && userEmail === FEEDBACK_ADMIN_EMAIL) {
    return renderPageWithHeader(
      <ThroughputReport
        onBackToApp={closeAllViews}
      />,
      { viewId: 'reports' }
    );
  }

  // --------------------------
  // PERFORMANCE REPORT VIEW (Mark only)
  // --------------------------
  if (showPerformanceReport && userEmail === FEEDBACK_ADMIN_EMAIL) {
    return renderPageWithHeader(
      <PerformanceReport
        onBackToApp={closeAllViews}
      />,
      { viewId: 'reports' }
    );
  }

  // --------------------------
  // FEEDBACK DASHBOARD VIEW (Mark only)
  // --------------------------
  if (showFeedbackDashboard && userEmail === FEEDBACK_ADMIN_EMAIL) {
    return renderPageWithHeader(
      <FeedbackDashboard
        onBackToApp={closeAllViews}
        userEmail={userEmail}
      />,
      { viewId: 'feedback-dashboard' }
    );
  }

  // --------------------------
  // WORK INSTRUCTIONS PAGE (Test)
  // --------------------------
  if (showWorkInstructions) {
    return renderPageWithHeader(
      <WorkInstructionsPage
        onClose={closeAllViews}
      />,
      { viewId: 'work-instructions' }
    );
  }

  // --------------------------
  // DATA QUALITY DASHBOARD VIEW (Mark only)
  // --------------------------
  if (showDataQuality && userEmail === FEEDBACK_ADMIN_EMAIL) {
    return renderPageWithHeader(
      <div className="flex-1 bg-gray-900 text-white overflow-auto">
        <div className="max-w-7xl mx-auto p-4">
          <DataQualityDashboard />
        </div>
      </div>,
      { viewId: 'data-quality' }
    );
  }

  // --------------------------
  // NOTIFICATION ADMIN (Admin only)
  // --------------------------
  if (showNotificationAdmin && userEmail === FEEDBACK_ADMIN_EMAIL) {
    return renderPageWithHeader(
      <NotificationAdmin
        token={sessionStorage.getItem("msal.backendToken")}
      />,
      { viewId: 'notification-admin' }
    );
  }

  // --------------------------
  // ADMIN TICKETS VIEW (Mark only)
  // --------------------------
  if (showAdminTickets && userEmail === FEEDBACK_ADMIN_EMAIL) {
    return renderPageWithHeader(
      <AdminTickets
        onBackToApp={closeAllViews}
      />,
      { viewId: 'admin-tickets' }
    );
  }

  // --------------------------
  // ADMIN USER MANAGEMENT (Beta users)
  // --------------------------
  if (showAdminUserManagement && isFeatureEnabled(FEATURES.ADMIN_USER_MANAGEMENT, userEmail)) {
    return renderPageWithHeader(
      <AdminUserManagement
        onBackToApp={closeAllViews}
      />,
      { viewId: 'admin-users' }
    );
  }

  // --------------------------
  // PA QUEUE (Production Art Queue - Beta Prototype)
  // --------------------------
  if (showPAQueue) {
    return renderPageWithHeader(
      <PAQueue
        employeeId={user?.id}
        displayName={user?.name || userEmail?.split('@')[0]}
        token={sessionStorage.getItem("msal.backendToken")}
        onSelectProject={(wo) => setSelectedProject(wo)}
        onReportIssue={handleOpenReprintRemake}
      />,
      { viewId: 'pa-queue' }
    );
  }

  // --------------------------
  // ASSEMBLY QUEUE
  // --------------------------
  if (showAssemblyQueue) {
    return renderPageWithHeader(
      <AssemblyQueue
        employeeId={user?.id}
        displayName={user?.name || userEmail?.split('@')[0]}
        token={sessionStorage.getItem("msal.backendToken")}
        onSelectProject={(wo) => setSelectedProject(wo)}
        onReportIssue={handleOpenReprintRemake}
      />,
      { viewId: 'assembly-queue' }
    );
  }

  // --------------------------
  // SHIPPING QUEUE (placeholder)
  // --------------------------
  if (showShippingQueue) {
    return renderPageWithHeader(
      <div className="flex-1 bg-[#0a0a0a] text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-purple-400 mb-2">Shipping Queue</h2>
          <p className="text-gray-400">Coming soon...</p>
        </div>
      </div>,
      { viewId: 'shipping-queue' }
    );
  }

  // --------------------------
  // PM DASHBOARD VIEW (temporarily hidden)
  // --------------------------
  // if (showPMDashboard && isPM && selectedWorkCenter === "PM") {
  //   return renderPageWithHeader(
  //     <PMDashboard
  //       onBackToApp={closeAllViews}
  //       initialQuote={selectedQuote}
  //       initialOrder={selectedOrder}
  //     />,
  //     { viewId: 'pm-dashboard' }
  //   );
  // }

  // --------------------------
  // ORDER VIEW (full page view of order jobs)
  // --------------------------
  if (showOrderView && selectedOrder) {
    return renderPageWithHeader(
      <OrderView
        order={selectedOrder}
        quote={selectedQuote}
        jobs={jobs}
        activeSessions={activeSessions}
        selectedOperation={selectedOperation}
        onBack={closeAllViews}
        onJobClick={(job) => {
          setSelectedJob(job);
          closeAllViews();
        }}
        onStartJob={handleStartJob}
        onPauseJob={handlePauseJob}
        onResumeJob={handleResumeJob}
        onCompleteJob={handleCompleteJob}
        onOperationSelect={handleOperationSelect}
      />,
      { viewId: 'work' }
    );
  }

  // --------------------------
  // MAIN APP LAYOUT
  // --------------------------
  const appContent = (
    <PinnedQuotesProvider>
    {/* Update notification banner - appears at top when new version available */}
    <UpdateBanner />

    <div className="min-h-screen bg-[#1a1a1a] text-white flex">
      {/* MAIN CONTENT — now shifts left when drawer is open */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          selectedJob ? "mr-[400px]" : ""
        }`}
      >
        {/* STICKY HEADER WITH CONTEXT */}
        <div className="sticky top-0 z-50">
          {/* MAIN HEADER - Clean, focused navigation */}
          <AppHeader
            user={user}
            userEmail={userEmail}
            isPM={isPM}
            isAdmin={isAdmin}
            displayName={user?.name || userEmail?.split('@')[0]}
            activeSessionCount={activeSessionCount}
            batchSessionCount={batchSessionCount}
            activeView={activeView}
            onViewChange={setActiveView}
            setShowProjectSchedule={setShowProjectSchedule}
            setShowTeamView={setShowTeamView}
            setShowPADashboard={setShowPADashboard}
            setShowPAQueue={setShowPAQueue}
            setShowAssemblyQueue={setShowAssemblyQueue}
            setShowShippingQueue={setShowShippingQueue}
            setShowProductionSchedule={setShowProductionSchedule}
            setShowProductionView={setShowProductionView}
            setShowWorkOrderGenerator={setShowWorkOrderGenerator}
            setShowPMCommandCenter={setShowPMCommandCenter}
            setShowPMInterface={setShowPMInterface}
            setShowWorkOrderDemo={setShowWorkOrderDemo}
            setShowReportsDashboard={setShowReportsDashboard}
            setShowFeedbackDashboard={setShowFeedbackDashboard}
            setShowAdminTickets={setShowAdminTickets}
            setShowAdminUserManagement={setShowAdminUserManagement}
            setShowWorkInstructions={setShowWorkInstructions}
            setShowDataQuality={setShowDataQuality}
            setShowNotificationAdmin={setShowNotificationAdmin}
            setShowFeedbackModal={setShowFeedbackModal}
            onReprintRemake={handleOpenReprintRemake}
            NotificationSettings={NotificationSettings}
            isStaging={isStaging}
          />

          {/* CONTEXT BAR - Work Center, Quote, Order */}
          <div className="w-full bg-gray-900/95 backdrop-blur border-b border-gray-700">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-4 flex-wrap">
              {/* Work Center */}
              <WorkCenterSelector
                allowedWorkCenters={allowedWorkCenters}
                selectedWorkCenter={selectedWorkCenter}
                onSelect={handleWorkCenterSelect}
                compact={true}
              />

              {/* Quote */}
              {selectedQuote && (
                <div className="flex items-center gap-2 px-3 py-1 bg-gray-800 rounded-md border border-gray-600">
                  <span className="text-gray-400 text-xs">Quote:</span>
                  <span className="text-white text-sm font-medium">
                    {selectedQuote.quoteNumber}
                  </span>
                  {selectedQuote.projectName && (
                    <span className="text-gray-400 text-xs truncate max-w-[150px]">
                      {selectedQuote.projectName}
                    </span>
                  )}
                </div>
              )}

              {/* Order */}
              {selectedOrder && (
                <div className="flex items-center gap-2 px-3 py-1 bg-gray-800 rounded-md border border-gray-600">
                  <span className="text-gray-400 text-xs">Order:</span>
                  <span className="text-white text-sm font-medium">
                    {selectedOrder.orderNumber}
                  </span>
                  {selectedOrder.projectName && (
                    <span className="text-gray-400 text-xs truncate max-w-[150px]">
                      {selectedOrder.projectName}
                    </span>
                  )}
                </div>
              )}


              {/* Work Order Button */}
              {selectedOrder && (
                selectedOrder.workOrderUrl ? (
                  <a
                    href={selectedOrder.workOrderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-md border border-blue-500 text-white text-sm font-medium transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Work Order
                  </a>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1 bg-gray-700 rounded-md border border-gray-600 text-gray-400 text-sm cursor-not-allowed">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    No work order available
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* MAIN STACK */}
        <main className="max-w-7xl mx-auto w-full px-4 py-6 flex flex-col gap-4">

          {/* MY WORK - Unified section for pinned projects and active sessions */}
          <MyWork
            allQuotes={allQuotes}
            activeSessions={activeSessions}
            onSelectQuote={(quote) => {
              if (quote) {
                handleQuoteSelect(quote);
              }
            }}
            onNavigateToJob={async ({ quoteNumber, orderNumber }) => {
              console.log("🔀 onNavigateToJob:", { quoteNumber, orderNumber, currentQuote: selectedQuote?.quoteNumber });

              // Navigate to the quote/order
              if (quoteNumber) {
                // Check if this is the same quote - if so, we need to directly fetch and set the order
                const isSameQuote = selectedQuote?.quoteNumber?.toString() === quoteNumber?.toString();
                console.log("🔀 isSameQuote:", isSameQuote);

                if (!isSameQuote) {
                  // Different quote - set it and use pending order mechanism
                  console.log("🔀 Setting new quote and pending order");
                  if (orderNumber) {
                    sessionStorage.setItem("3dcolor-pending-order", orderNumber);
                  }
                  setSelectedQuote({ quoteNumber });
                } else if (orderNumber) {
                  // Same quote - directly fetch and set the order to avoid the race condition
                  console.log("🔀 Same quote - fetching order directly");
                  const token = sessionStorage.getItem("msal.backendToken");
                  if (token) {
                    try {
                      const res = await fetch(
                        `/api/orders?quoteNumber=${encodeURIComponent(quoteNumber)}`,
                        { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
                      );
                      if (res.ok) {
                        const data = await res.json();
                        const orders = data.orders || [];
                        const targetOrder = orders.find(o => String(o.orderNumber) === String(orderNumber));
                        if (targetOrder) {
                          console.log("🔀 Found and setting order:", targetOrder.orderNumber);
                          setSelectedOrder(targetOrder);
                        } else {
                          console.log("🔀 Order not found in list:", orderNumber, "Available:", orders.map(o => o.orderNumber));
                        }
                      }
                    } catch (err) {
                      console.error("Error fetching order for navigation:", err);
                    }
                  }
                }
              }
            }}
            onPauseJob={handlePauseJob}
            onResumeJob={handleResumeJob}
            onCompleteJob={handleCompleteJob}
            pendingTicketsReady={pendingTicketsReady}
            onAssignPendingTickets={() => setShowAssignModal(true)}
          />

          {/* PENDING QUOTE MODE BANNER */}
          {pendingQuoteMode && (
            <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-amber-300">Pending Quote Mode</div>
                  <div className="text-sm text-amber-200/70">
                    Track time against a quote that doesn't have orders yet. Select a quote and operation, then click "Start Timer".
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selectedQuote && selectedOperation && (
                  <button
                    onClick={handleStartPendingQuoteClick}
                    disabled={pendingQuoteStarting}
                    className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-green-800 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {pendingQuoteStarting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Starting...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Start Timer
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setPendingQuoteMode(false)}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* SELECTORS */}
          <section
            className={`grid grid-cols-1 lg:grid-cols-3 gap-4 ${selectorDimClass}`}
          >
            <div className={card}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold uppercase tracking-wide text-purple-300">QUOTE</h2>
                {!pendingQuoteMode && (
                  <button
                    onClick={() => setPendingQuoteMode(true)}
                    className="text-xs text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
                    title="Track time against a quote without orders"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Pending Quote
                  </button>
                )}
              </div>
              <QuoteSelector
                onSelect={handleQuoteSelect}
                value={selectedQuote}
                onQuotesLoaded={setAllQuotes}
                pendingQuoteMode={pendingQuoteMode}
              />
            </div>

            <div className={card}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-purple-300 mb-1">ORDER</h2>
              {pendingQuoteMode ? (
                <div className="h-[200px] flex items-center justify-center text-gray-500 text-sm border border-dashed border-gray-700 rounded-lg">
                  <div className="text-center">
                    <div className="text-amber-400 font-medium">Pending Quote Mode</div>
                    <div className="text-xs mt-1">Orders not required</div>
                  </div>
                </div>
              ) : (
                <OrderSelector
                  quote={selectedQuote}
                  selected={selectedOrder}
                  onSelect={setSelectedOrder}
                />
              )}
            </div>

            <div className={card}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-purple-300 mb-1">OPERATION</h2>
              <OperationSelector
                order={pendingQuoteMode ? null : selectedOrder}
                onSelect={handleOperationSelect}
                selected={selectedOperation}
                workCenter={selectedWorkCenter}
                showAllOperations={pendingQuoteMode}
              />
            </div>
          </section>

          {/* JOB TRACKER */}
          <section className={card}>
            <h2 className="text-sm font-bold uppercase tracking-wide text-purple-300 mb-2">
              JOBS IN ORDER{" "}
              {selectedOrder?.orderNumber && (
                <span className="text-white">#{selectedOrder.orderNumber}</span>
              )}
            </h2>
            <JobTracker
              jobs={jobs}
              selectedOrder={selectedOrder}
              selectedOperation={selectedOperation}
              selectedJob={selectedJob}
              onJobClick={handleJobClick}
              onStartJob={handleStartJob}
              onInstantComplete={handleInstantComplete}
              activeSessions={activeSessions}
              onPauseJob={handlePauseJob}
              onResumeJob={handleResumeJob}
              onCompleteJob={handleCompleteJob}
              selectedJobIds={selectedJobIds}
              onSelectionChange={setSelectedJobIds}
            />
          </section>

          {/* TODAY'S SESSIONS */}
          <TodaysSessions />

          {/* VIEW HISTORY LINK */}
          <div className="flex justify-center py-4">
            <button
              onClick={() => setShowSessionHistory(true)}
              className="flex items-center gap-2 text-gray-400 hover:text-purple-400 transition-colors text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View Session History
            </button>
          </div>
        </main>
      </div>

      {/* FIXED JOB DETAIL DRAWER */}
      <div className="fixed right-0 top-0 h-screen z-[60]">
        <JobDetailDrawer
          job={selectedJob}
          operation={selectedOperation}
          selectedRoutingStep={selectedRoutingStep}
          routing={routing}
          routingLoading={routingLoading}
          routingError={routingError}
          activeSessions={activeSessions}
          onRoutingStepSelect={handleRoutingStepSelect}
          onClose={() => setSelectedJob(null)}
          onStartJob={handleStartJob}
          onPauseJob={handlePauseJob}
          onResumeJob={handleResumeJob}
          onCompleteJob={handleCompleteJob}
        />
      </div>

      {/* SINGLE COMPLETE MODAL */}
      {completeModal.open && (
        <CompleteModal
          job={completeModal.job}
          operation={selectedOperation}
          session={activeSessions[completeModal.job.id]}
          autoQuantity={completeModal.autoQuantity}
          onCancel={handleCancelComplete}
          onSubmit={handleConfirmComplete}
        />
      )}

      {/* MULTI-COMPLETE MODAL */}
      {multiCompleteModal.open && (
        <MultiCompleteModal
          open={multiCompleteModal.open}
          jobs={multiCompleteModal.jobs}
          operationCode={selectedOperation?.operationCode}
          autoQuantity={multiCompleteModal.autoQuantity}
          onCancel={handleCancelMultiComplete}
          onSubmit={handleConfirmMultiComplete}
        />
      )}

      {/* REPRINT MODAL (for jobs that were previously printed) */}
      {reprintModal.open && (
        <ReprintModal
          job={reprintModal.job || reprintModal.jobs[0]}
          printCount={reprintModal.printCount}
          onSelect={handleReprintSelect}
          onCancel={handleReprintCancel}
        />
      )}

      {/* PRINTER SELECT MODAL (for PRINT-MACH operations) */}
      {printerModal.open && (
        <PrinterSelectModal
          job={printerModal.job || printerModal.jobs[0]}
          onSelect={handlePrinterSelect}
          onCancel={handlePrinterCancel}
        />
      )}

      {/* REWORK CAUSE MODAL (for ALL REWORK/REVISION operations) */}
      {reworkCauseModal.open && (
        <ReworkCauseModal
          operationType={reworkCauseModal.operationType}
          job={reworkCauseModal.job}
          jobs={reworkCauseModal.jobs}
          isMulti={reworkCauseModal.isMulti}
          onSubmit={handleReworkCauseSubmit}
          onCancel={handleReworkCauseCancel}
        />
      )}

      {/* REWORK TYPE MODAL (for PA users doing REWORK operations - after cause is captured) */}
      {reworkModal.open && (
        <ReworkTypeModal
          job={reworkModal.job || reworkModal.jobs[0]}
          onSelect={handleReworkSelect}
          onCancel={handleReworkCancel}
        />
      )}

      {/* PENDING QUOTE COMPLETE MODAL */}
      {pendingQuoteCompleteModal.open && (
        <PendingQuoteCompleteModal
          ticket={pendingQuoteCompleteModal.ticket}
          onComplete={handlePendingQuoteComplete}
          onCancel={handleCancelPendingQuoteComplete}
        />
      )}

      {/* SUCCESS TOAST */}
      {successToast.show && (
        <SuccessToast
          message={successToast.message}
          onClose={() => setSuccessToast({ show: false, message: "" })}
        />
      )}

      {/* PENDING QUOTE CONFIRM MODAL */}
      {pendingQuoteConfirmModal && (
        <PendingQuoteConfirmModal
          quote={selectedQuote}
          operation={selectedOperation}
          onConfirm={handleConfirmStartPendingQuote}
          onCancel={() => setPendingQuoteConfirmModal(false)}
        />
      )}

      {/* ASSIGN TICKET MODAL */}
      {showAssignModal && (
        <AssignTicketModal
          tickets={pendingTicketsData}
          onClose={() => {
            setShowAssignModal(false);
            // Track dismissed quote so we don't re-prompt on same selection
            if (selectedQuote?.quoteNumber) {
              dismissedPendingQuotesRef.current.add(String(selectedQuote.quoteNumber));
            }
          }}
          onAssign={(ticketId) => {
            // Remove assigned ticket from list and update count
            setPendingTicketsData((prev) => prev.filter((t) => t.id !== ticketId));
            setPendingTicketsReady((prev) => Math.max(0, prev - 1));
            // Trigger refresh
            window.dispatchEvent(new CustomEvent("time-updated"));
          }}
        />
      )}

      {/* PM INSTANT-COMPLETE CONFIRMATION MODAL */}
      <PMInstantCompleteModal
        open={pmCompleteModal.open}
        job={pmCompleteModal.job}
        operation={selectedOperation}
        order={selectedOrder}
        onCancel={handleCancelPmComplete}
        onConfirm={handleConfirmPmComplete}
      />

      {/* KEYBOARD SHORTCUTS HELP (Press ? to toggle) */}
      <KeyboardShortcutsHelp shortcuts={shortcuts} />

      {/* FEEDBACK MODAL */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        employeeId={user?.localAccountId}
        employeeName={user?.name || user?.username}
        workCenter={selectedWorkCenter}
        currentPage="Time Tracker"
      />

      {/* REWORK MODAL (Legacy) */}
      <ReworkModal
        isOpen={showReworkModal}
        onClose={() => setShowReworkModal(false)}
        employeeId={user?.localAccountId}
        displayName={user?.name || user?.username}
        token={sessionStorage.getItem("msal.backendToken")}
      />

      {/* REPRINT/REMAKE MODAL (Unified) */}
      <ReprintRemakeModal
        isOpen={reprintRemakeModal.open}
        onClose={handleCloseReprintRemake}
        preselectedQuote={reprintRemakeModal.preselectedQuote}
        preselectedJobs={reprintRemakeModal.preselectedJobs}
        workCenter={reprintRemakeModal.workCenter}
        currentUser={{
          employeeId: user?.localAccountId,
          displayName: user?.name || user?.username,
        }}
        token={sessionStorage.getItem("msal.backendToken")}
      />
    </div>
    </PinnedQuotesProvider>
  );

  return Sentry.withErrorBoundary(appContent, {
    fallback: (
      <div className="min-h-screen bg-[#1a1a1a] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
          <p className="text-gray-400 mb-6">An unexpected error occurred. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-md"
          >
            Refresh Page
          </button>
        </div>
      </div>
    ),
  });
}
